import { SearchInput } from 'components/SearchInput/SearchInput'
import { useEffect, useState } from 'react'
import { partition, uniqBy } from 'lodash'

import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import { useProperties as useReduxProperties } from 'hooks/useProperties'

import { DefaultType, PropertyType, Property } from 'api/clients/PropertyClient'
import { useFilterProperties } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/Filter/hooks/useFilterProperties'
import { ReactComponent as CheckIcon } from 'assets/icons/check16.svg'
import ExistingPropertyActionItem from 'common/PropertyActions/ExistingPropertyActionItem'
import { Button } from 'components/Button'
import Styles from './PropertyFinder.module.scss'
import cx from 'classnames'

const GroupPropertyFinder = ({
  onPropertySelected,
  selectedProperty,
  className,
}: {
  onPropertySelected: (property: Property) => void
  selectedProperty?: Property
  className?: string
}) => {
  const suggest = selectedProperty ? [] : [PropertyType.SingleSelect, PropertyType.Date, PropertyType.Person]
  const { filterByName } = useReduxProperties()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [properties, hiddenPropertyIds] = useFilterProperties({
    excludedProperties: [DefaultType.LastOpened, DefaultType.Shared, DefaultType.Name],
  })
  const [showWorkspaceProperties, setShowWorkspaceProperties] = useState(false)

  useEffect(() => {
    if (searchQuery.length > 0) setShowWorkspaceProperties(true)
  }, [searchQuery.length])

  useProperties({}, { enabled: true }, { pushToStore: true })

  const filteredProperties = filterByName(properties, searchQuery)
  const filteredAndOrderedProperties =
    selectedProperty && filteredProperties.includes(selectedProperty)
      ? uniqBy([selectedProperty].concat(filteredProperties), 'id')
      : filteredProperties

  const [suggestedProperties, otherProperties] = partition(filteredAndOrderedProperties, (property: Property) => {
    return (
      (!suggest.length && !!property.default_type) ||
      property.id === selectedProperty?.id ||
      (suggest.includes(property.type as PropertyType) &&
        (property.default_type ? ![DefaultType.Updated, DefaultType.Created].includes(property.default_type) : true))
    )
  })

  const [moreWorkspaceProperties, moreProperties] = partition(
    otherProperties,
    (p) => !p.is_default && !p.scope_project_id && !p.default_type,
  )

  const sortedMoreProperties = moreProperties.sort(
    (a, b) => hiddenPropertyIds.indexOf(a.id) - hiddenPropertyIds.indexOf(b.id),
  )

  const renderProperty = (property: Property) => (
    <Button
      key={`property-${property.id}`}
      variant="tertiary"
      size="lg"
      onPress={() => onPropertySelected(property)}
      className={cx(Styles.propertyRow, { [Styles.selected]: selectedProperty?.id === property.id })}
      data-testid={`property-option-${property.id}`}
    >
      <ExistingPropertyActionItem key={`${property.id}`} property={property} />
      {property.id && selectedProperty?.id === property.id && <CheckIcon data-testid="check-icon" />}
    </Button>
  )

  return (
    <div className={className}>
      <div className={Styles.searchInputWrap}>
        <SearchInput
          autoFocus={true}
          className={Styles.searchInput}
          placeholder="Find a property..."
          onChange={(searchQuery: string) => setSearchQuery(searchQuery)}
          debounceInterval={300}
          data-testid="property-finder-search-input"
        />
      </div>

      <div className={Styles.searchedProps}>
        {suggestedProperties?.map((property: Property) => renderProperty(property))}

        {!!otherProperties.length && (
          <>
            {showWorkspaceProperties ? (
              <>
                {sortedMoreProperties?.map((property: Property) => renderProperty(property))}
                {!!moreWorkspaceProperties.length && (
                  <div className={Styles.searchedProps}>
                    <div className={Styles.workspaceHeader}>Workspace Properties</div>
                    {moreWorkspaceProperties?.map((property: Property) => renderProperty(property))}
                  </div>
                )}
              </>
            ) : (
              <div className={Styles.showWorkspaceButton} onClick={() => setShowWorkspaceProperties(true)}>
                Show more...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export { GroupPropertyFinder }
